// Global
import React from 'react';
import { ComponentRendering, Text } from '@sitecore-jss/sitecore-jss-nextjs';

// Lib
import { Marketing } from 'lib/templates/Feature.BOKF.model';

// Local
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import Button from 'helpers/Button/Button';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import classNames from 'classnames';

import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';

type DisplayOptions = { Value: { value: string } }; // using this to overwrite incomplete leprechaun types

type HeroProps = {
  rendering: ComponentRendering;
  params: {
    HeadingType: {
      Tag: {
        value: string;
      };
      Styles: {
        value: string;
      };
    };
    'Display Options': {
      Value: {
        value: string;
      };
    };
    ImageBleed?: string;
    EnableFluidWidth?: string;
    AnchorId?: string;
  };
  fields: {
    HideImageOptions?: {
      fields: {
        Value: {
          value: string;
        };
      };
    };
    AdditionalLinkFormat?: {
      fields: {
        Value: {
          value: string;
        };
      };
    };
  };
} & Marketing.DataTemplates.Hero;

const Hero = (props: HeroProps): JSX.Element => {
  const hideImageOptions = props.fields.HideImageOptions?.fields.Value.value;
  const imageBleedParam = props.params.ImageBleed?.length ? 'imgBleed' : 'object-contain';
  const fluidWidthParam = props.params.EnableFluidWidth?.length
    ? 'imgFluidWidth'
    : 'container-wide';

  const { currentScreenWidth } = useCurrentScreenType();

  const anchor = props.params?.AnchorId ?? '';
  const imageBleed = classNames(imageBleedParam);
  const hideImage = classNames(hideImageOptions);

  const displayOptions = props.params['Display Options']?.Value?.value ?? '';
  const hasBackground = !displayOptions?.includes('hero--noBkgd');
  const imageLeft = displayOptions?.includes('hero--imgLeft');
  const headingTag = props.params?.HeadingType?.Tag.value.length
    ? props.params?.HeadingType?.Tag.value
    : 'h2';

  const additionalLinkFormat =
    props.fields.AdditionalLinkFormat?.fields?.Value?.value ||
    'bokf-additional-links-format-vertical';

  const hasTitleUnderline =
    props.params?.HeadingType?.Styles?.value.includes('title-border') || false;
  const hasThickUnderline = props.params?.HeadingType?.Styles?.value.includes('thick');

  const { CallToAction, SecondaryCallToAction, PrimarySubLink, SecondarySubLink, TertiarySubLink } =
    props.fields || {};

  const hasButtons = CallToAction?.value?.text || SecondaryCallToAction?.value?.text;

  const hasLinks =
    PrimarySubLink?.value?.href || SecondarySubLink?.value?.href || TertiarySubLink?.value?.href;

  return (
    <section className={`component ${fluidWidthParam} spacing-md`} id={anchor}>
      <div
        className={`${
          hasBackground
            ? 'border border-light-stroke-primary bg-light-background-neutral-variant'
            : ''
        }`}
      >
        <div
          className={`flex flex-col lg:flex-row gap-8 min-h-[26.25rem] ${
            imageLeft ? 'justify-start' : 'justify-end'
          }`}
        >
          <div
            className={`flex flex-col pb-8 lg:py-8 px-4 lg:px-0 gap-y-6 justify-center text-center lg:text-left ${
              imageLeft
                ? `order-last lg:pr-[clamp(2rem,_-8.75rem_+_13.75vw,_10.313rem)] lg:basis-1/2`
                : `order-last lg:order-first lg:pl-[clamp(2rem,_-8.75rem_+_13.75vw,_10.313rem)] lg:basis-1/2`
            }`}
          >
            <RichTextA11yWrapper className="rtaw" tag={headingTag} field={props.fields?.Title} />
            {hasTitleUnderline && (
              <div
                className={`${
                  hasThickUnderline ? 'border-b-[5px]' : 'border-b-[3px]'
                } mt-2 border-light-stroke-secondary-brand w-[3rem]`}
              ></div>
            )}
            <div className="flex flex-col gap-y-6 items-center lg:items-start">
              <RichTextA11yWrapper
                className="p2 text-light-fill-primary-variant rtaw"
                field={props.fields?.Text}
              />
              {hasButtons && (
                <div className="w-full flex flex-row flex-wrap justify-center @5xl:justify-start gap-y-3 gap-x-6">
                  {props.fields?.CallToAction?.value?.text && (
                    <Button
                      type="default"
                      title={props.fields?.CallToAction?.value?.text}
                      field={props.fields?.CallToAction}
                    />
                  )}
                  {props.fields?.SecondaryCallToAction?.value?.text && (
                    <Button
                      type="secondary"
                      title={props.fields?.SecondaryCallToAction?.value?.text}
                      field={props.fields?.SecondaryCallToAction}
                    />
                  )}
                </div>
              )}
              {hasLinks && (
                <div className={`flex ${additionalLinkFormat}`}>
                  <Text
                    tag="p"
                    className="text-grey-1 flex items-center justify-center @5xl:justify-start"
                    field={props?.fields?.SublinkTitle}
                  />
                  <div className="hero-link-list">
                    {props.fields?.PrimarySubLink?.value.href && (
                      <LinkWrapper
                        field={props.fields?.PrimarySubLink}
                        className="link p2-medium"
                      ></LinkWrapper>
                    )}
                    {props.fields?.SecondarySubLink?.value.href && (
                      <LinkWrapper
                        field={props.fields?.SecondarySubLink}
                        className={`link p2-medium ${
                          additionalLinkFormat === 'bokf-additional-links-format-horizontal'
                            ? 'pl-4'
                            : ''
                        }`}
                      ></LinkWrapper>
                    )}
                    {props.fields?.TertiarySubLink?.value.href && (
                      <LinkWrapper
                        field={props.fields?.TertiarySubLink}
                        className={`link p2-medium ${
                          additionalLinkFormat === 'bokf-additional-links-format-horizontal'
                            ? 'pl-4'
                            : ''
                        }`}
                      ></LinkWrapper>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={`basis-1/2 hero ${hideImage}`}>
            {currentScreenWidth >= getBreakpoint('desktop') &&
              props.fields?.DesktopImage?.value?.src?.length && (
                <ImageWrapper
                  className={`w-full h-full ${imageBleed} ${
                    (props.fields?.DesktopImagePosition?.fields as DisplayOptions)?.Value?.value ||
                    ''
                  }`}
                  field={props.fields?.DesktopImage}
                />
              )}

            {currentScreenWidth >= getBreakpoint('tablet') &&
              currentScreenWidth < getBreakpoint('desktop') && (
                <ImageWrapper
                  className={`w-full h-full ${imageBleed} ${
                    (props.fields?.TabletImagePosition?.fields as DisplayOptions)?.Value?.value ||
                    ''
                  }`}
                  field={
                    props.fields?.TabletImage?.value?.src?.length
                      ? props.fields?.TabletImage
                      : props.fields?.DesktopImage
                  }
                />
              )}

            {currentScreenWidth < getBreakpoint('tablet') && (
              <ImageWrapper
                className={`w-full h-full ${imageBleed} ${
                  (props.fields?.MobileImagePosition?.fields as DisplayOptions)?.Value?.value || ''
                }`}
                field={
                  props.fields?.MobileImage?.value?.src?.length
                    ? props.fields?.MobileImage
                    : props.fields?.DesktopImage
                }
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
